// Topic model for the offline conversation engine — a port of the Android
// build's `tutor/Conversation.kt`.
//
// Each topic carries its own reactions and follow-up questions so the tutor can
// keep a thread going for several turns without repeating itself: the offline
// stand-in for the model-generated replies the original React Native build
// fetched from an API.

export type TopicId =
  | 'GREETING' | 'FEELINGS' | 'WORK' | 'STUDY' | 'FAMILY' | 'FOOD' | 'TRAVEL'
  | 'HOBBY' | 'MOVIES' | 'MUSIC' | 'SPORTS' | 'WEATHER' | 'HEALTH' | 'TECH'
  | 'SHOPPING' | 'PLANS' | 'PETS' | 'GENERAL';

export interface Topic {
  id: TopicId;
  keywords: readonly string[];
  reactions: readonly string[];
  questions: readonly string[];
}

/** Declaration order matters: `detect` keeps the first topic with the top score. */
export const TOPICS: readonly Topic[] = [
  {
    id: 'GREETING',
    keywords: [
      'hello', 'hi', 'hey', 'morning', 'afternoon', 'evening', 'greetings',
      'nice to meet you', 'how are you', "what's up",
    ],
    reactions: [
      "Hi there! It's really good to hear from you.",
      "Hello! I'm glad you're here to practise today.",
      'Hey! Great to see you back.',
    ],
    questions: [
      'How has your day been so far?',
      "What's the first thing you did this morning?",
      'How are you feeling today — energetic or a bit tired?',
    ],
  },
  {
    id: 'FEELINGS',
    keywords: [
      'tired', 'happy', 'sad', 'angry', 'excited', 'nervous', 'bored', 'stressed',
      'worried', 'lonely', 'fine', 'good', 'great', 'bad', 'okay', 'sleepy',
      'feel', 'feeling', 'mood', 'upset', 'relaxed',
    ],
    reactions: [
      "Thanks for telling me how you feel — that's a great habit in English.",
      'I hear you. Talking about feelings is one of the hardest things in a new language.',
      'That makes sense to me.',
    ],
    questions: [
      'What made you feel that way?',
      'What usually helps when you feel like that?',
      'Has it been like that all week, or just today?',
    ],
  },
  {
    id: 'WORK',
    keywords: [
      'work', 'job', 'office', 'boss', 'colleague', 'meeting', 'project',
      'career', 'company', 'salary', 'interview', 'deadline', 'client',
      'team', 'manager', 'business',
    ],
    reactions: [
      "Work vocabulary is so useful — you're practising exactly the right words.",
      'That sounds like a busy work life.',
      'Talking about your job is one of the most common conversations in English.',
    ],
    questions: [
      'What does a typical day at work look like for you?',
      "What's the best part of your job?",
      "Is there anything at work you'd like to change?",
      'Do you have to use English at work?',
    ],
  },
  {
    id: 'STUDY',
    keywords: [
      'study', 'school', 'university', 'college', 'class', 'teacher', 'exam',
      'test', 'homework', 'student', 'learn', 'english', 'lesson', 'course',
      'grammar', 'vocabulary', 'book', 'degree',
    ],
    reactions: [
      'Studying takes real discipline — well done for keeping at it.',
      'Nice, learning is a great topic to practise.',
      "That's a solid answer about studying.",
    ],
    questions: [
      'How long have you been studying English?',
      "What's the hardest part of English for you — speaking, listening, or grammar?",
      'Where do you usually study?',
      'What made you start learning English?',
    ],
  },
  {
    id: 'FAMILY',
    keywords: [
      'family', 'mother', 'father', 'mom', 'dad', 'sister', 'brother', 'son',
      'daughter', 'wife', 'husband', 'parents', 'children', 'kids', 'grandma',
      'grandpa', 'cousin', 'baby', 'married',
    ],
    reactions: [
      'Family is such a warm topic — thanks for sharing.',
      "That's lovely to hear about your family.",
      'Family words are really useful in everyday English.',
    ],
    questions: [
      'How big is your family?',
      'What do you usually do together on weekends?',
      'Who are you closest to in your family?',
    ],
  },
  {
    id: 'FOOD',
    keywords: [
      'food', 'eat', 'eating', 'ate', 'lunch', 'dinner', 'breakfast', 'cook',
      'cooking', 'restaurant', 'coffee', 'tea', 'pizza', 'rice', 'chicken',
      'hungry', 'delicious', 'recipe', 'meal', 'snack', 'dessert', 'drink',
    ],
    reactions: [
      'Mmm, food is my favourite topic to practise!',
      'That sounds delicious.',
      'Food vocabulary always comes in handy when you travel.',
    ],
    questions: [
      "What's your favourite dish to cook?",
      'Do you prefer eating at home or going out?',
      'What did you have for lunch today?',
      "Is there a food from another country you'd love to try?",
    ],
  },
  {
    id: 'TRAVEL',
    keywords: [
      'travel', 'trip', 'holiday', 'vacation', 'flight', 'airport', 'hotel',
      'country', 'city', 'abroad', 'visit', 'tourist', 'beach', 'mountain',
      'train', 'passport', 'journey', 'sightseeing',
    ],
    reactions: [
      'Travel stories are perfect speaking practice.',
      'That sounds like a great trip!',
      'I love hearing about places people have been.',
    ],
    questions: [
      "What's the most beautiful place you've ever visited?",
      'Where would you go tomorrow if you could go anywhere?',
      'Do you prefer city trips or nature trips?',
      'What do you always pack in your suitcase?',
    ],
  },
  {
    id: 'HOBBY',
    keywords: [
      'hobby', 'hobbies', 'free time', 'weekend', 'read', 'reading', 'draw',
      'painting', 'photography', 'garden', 'game', 'gaming', 'knitting',
      'collect', 'fun', 'relax', 'enjoy',
    ],
    reactions: [
      'Hobbies are a great way to practise describing things.',
      'That sounds like a fun way to spend your time.',
      'Nice — talking about hobbies is a classic conversation starter.',
    ],
    questions: [
      'How did you get into that?',
      'How often do you get to do it?',
      "Is there a new hobby you'd like to try this year?",
    ],
  },
  {
    id: 'MOVIES',
    keywords: [
      'movie', 'movies', 'film', 'series', 'netflix', 'watch', 'watched', 'show',
      'actor', 'actress', 'drama', 'comedy', 'cinema', 'episode', 'documentary',
    ],
    reactions: [
      'Films are brilliant for listening practice.',
      "That sounds like something I'd enjoy too.",
      'Good choice — describing a story is great speaking practice.',
    ],
    questions: [
      'What was the last thing you watched?',
      'Do you watch with subtitles or without?',
      'Which character did you like the most, and why?',
    ],
  },
  {
    id: 'MUSIC',
    keywords: [
      'music', 'song', 'songs', 'sing', 'singing', 'guitar', 'piano', 'band',
      'concert', 'album', 'playlist', 'listen to music', 'singer',
    ],
    reactions: [
      'Music is a great way to pick up natural English phrases.',
      "That's a nice choice.",
      'Songs teach rhythm and pronunciation better than any textbook.',
    ],
    questions: [
      'What song have you had on repeat lately?',
      'Do you understand the lyrics when you listen in English?',
      'Have you ever been to a live concert?',
    ],
  },
  {
    id: 'SPORTS',
    keywords: [
      'sport', 'sports', 'football', 'soccer', 'basketball', 'tennis', 'gym',
      'run', 'running', 'swim', 'swimming', 'yoga', 'match', 'team', 'exercise',
      'workout', 'baseball', 'cycling', 'hike', 'hiking',
    ],
    reactions: [
      'Staying active is great — and great vocabulary practice.',
      'Nice! Sports talk comes up constantly in small talk.',
      'That sounds like a good workout.',
    ],
    questions: [
      'How often do you train?',
      'Do you prefer team sports or exercising alone?',
      "Is there a sport you've always wanted to learn?",
    ],
  },
  {
    id: 'WEATHER',
    keywords: [
      'weather', 'rain', 'raining', 'sunny', 'snow', 'hot', 'cold', 'warm',
      'cloudy', 'windy', 'storm', 'humid', 'season', 'summer', 'winter',
      'spring', 'autumn', 'fall',
    ],
    reactions: [
      'Weather talk is the classic English small-talk opener.',
      'Sounds like typical weather for this time of year.',
      "Good — that's exactly the vocabulary native speakers use daily.",
    ],
    questions: [
      "What's your favourite season, and why?",
      'Does the weather change how you spend your day?',
      'Would you rather live somewhere hot or somewhere cold?',
    ],
  },
  {
    id: 'HEALTH',
    keywords: [
      'health', 'sick', 'ill', 'doctor', 'hospital', 'medicine', 'headache',
      'sleep', 'sleeping', 'diet', 'healthy', 'pain', 'cold', 'flu', 'dentist',
      'appointment',
    ],
    reactions: [
      "Health vocabulary is important — I'm glad we're practising it.",
      "I hope you're taking care of yourself.",
      "That's useful language for a doctor's visit.",
    ],
    questions: [
      'How many hours do you usually sleep?',
      'What do you do to stay healthy?',
      'Have you had to see a doctor in English before?',
    ],
  },
  {
    id: 'TECH',
    keywords: [
      'phone', 'computer', 'laptop', 'app', 'internet', 'software', 'code',
      'coding', 'programming', 'ai', 'technology', 'website', 'social media',
      'instagram', 'youtube', 'email', 'android',
    ],
    reactions: [
      'Tech words are mostly the same in English — handy for you!',
      "That's a modern topic, nicely done.",
      'Technology talk comes up a lot at work.',
    ],
    questions: [
      'Which app do you use the most?',
      'Do you think technology helps or hurts your focus?',
      "What's the last thing you learned online?",
    ],
  },
  {
    id: 'SHOPPING',
    keywords: [
      'shopping', 'buy', 'bought', 'shop', 'store', 'market', 'price', 'cheap',
      'expensive', 'clothes', 'shoes', 'dress', 'sale', 'money', 'spend',
    ],
    reactions: [
      'Shopping phrases are so practical when you travel.',
      'That sounds like a good find.',
      'Nice — bargaining and prices are useful English.',
    ],
    questions: [
      'What was the last thing you bought?',
      'Do you prefer shopping online or in a store?',
      'Are you a careful spender or an impulse buyer?',
    ],
  },
  {
    id: 'PLANS',
    keywords: [
      'tomorrow', 'next week', 'plan', 'plans', 'going to', 'will', 'future',
      'goal', 'dream', 'someday', 'hope', 'resolution', 'weekend',
    ],
    reactions: [
      'Talking about the future is great practice for "will" and "going to".',
      'That sounds like a solid plan.',
      'I like where this is going!',
    ],
    questions: [
      'What are you most looking forward to?',
      "What's one goal you'd like to reach this year?",
      'How will you make that happen?',
    ],
  },
  {
    id: 'PETS',
    keywords: [
      'dog', 'cat', 'pet', 'pets', 'puppy', 'kitten', 'animal', 'bird', 'fish',
      'hamster', 'rabbit',
    ],
    reactions: [
      'Pets are such a friendly topic to practise.',
      'Aww, that sounds lovely.',
      'Animals always make conversation easy.',
    ],
    questions: [
      "What's your pet's name, and what is it like?",
      'Did you have any pets when you were a child?',
      'Are you more of a dog person or a cat person?',
    ],
  },
  {
    id: 'GENERAL',
    keywords: [],
    reactions: [
      "That's interesting — thanks for sharing that with me.",
      'Nice, I like the way you explained that.',
      "Good, keep going — you're doing well.",
      'I see what you mean.',
    ],
    questions: [
      'Can you tell me a little more about that?',
      'Why is that important to you?',
      'How did that make you feel?',
      'What happened next?',
      'Can you give me an example?',
    ],
  },
];

export const GENERAL_TOPIC: Topic = TOPICS[TOPICS.length - 1]!;

/** Picks the topic with the most keyword hits in `text`; GENERAL when none match. */
export function detectTopic(text: string): Topic {
  const lower = ` ${text.toLowerCase().replace(/[^a-z' ]/g, ' ')} `;
  let best = GENERAL_TOPIC;
  let bestScore = 0;
  for (const topic of TOPICS) {
    if (topic.id === 'GENERAL') continue;
    const score = topic.keywords.filter((k) => lower.includes(` ${k} `)).length;
    if (score > bestScore) {
      bestScore = score;
      best = topic;
    }
  }
  return best;
}

/** Swaps first/second person so the tutor can echo the learner naturally. */
const REFLECTIONS: Readonly<Record<string, string>> = {
  i: 'you', me: 'you', my: 'your', mine: 'yours',
  myself: 'yourself', am: 'are', we: 'you', our: 'your',
  ours: 'yours', us: 'you', "i'm": "you're", "i've": "you've",
  "i'll": "you'll", "i'd": "you'd",
};

/** Kotlin's `String.trim(vararg chars)` — strips the given characters both ends. */
function trimChars(word: string, chars: string): string {
  let start = 0;
  let end = word.length;
  while (start < end && chars.includes(word.charAt(start))) start++;
  while (end > start && chars.includes(word.charAt(end - 1))) end--;
  return word.slice(start, end);
}

export function reflect(text: string): string {
  return text
    .split(' ')
    .map((word) => {
      const bare = trimChars(word, '.,!?').toLowerCase();
      const swap = REFLECTIONS[bare];
      // Kotlin's String.replace swaps every occurrence, not just the first.
      return swap === undefined ? word : word.toLowerCase().split(bare).join(swap);
    })
    .join(' ')
    .trim();
}
